import { useEffect, useEffectEvent, useId, useState } from "react";
import { fetchOpportunities, searchAccounts, type AccountHit, type OpportunityHit } from "./api.js";
import styles from "./AccountPicker.module.css";

export interface Selection {
  account: AccountHit | null;
  opportunityId: string;
}

export const NO_SELECTION: Selection = { account: null, opportunityId: "" };

/**
 * Choose an Account (search by name) and, optionally, one of its Opportunities. Staff only: the
 * names come from Salesforce.
 */
export function AccountPicker({
  selection,
  onChange,
  initialOpportunityId,
}: {
  selection: Selection;
  onChange: (s: Selection) => void;
  /** An Opportunity to select once the Account's Opportunities have loaded (from a deep link). */
  initialOpportunityId?: string;
}) {
  const searchId = useId();
  const oppId = useId();
  const [query, setQuery] = useState("");
  // Results are kept with the search text and Account they belong to, so a stale result is never
  // shown, and nothing is reset from inside an effect.
  const [found, setFound] = useState<{ query: string; hits: AccountHit[] } | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const [oppsFor, setOppsFor] = useState<{ accountId: string; opps: OpportunityHit[] } | null>(
    null,
  );
  const accountId = selection.account?.id;
  const term = query.trim();

  useEffect(() => {
    if (selection.account || term.length < 2) return;
    const ctl = new AbortController();
    const t = setTimeout(() => {
      searchAccounts(term, ctl.signal).then(
        (hits) => setFound({ query: term, hits }),
        (err: unknown) => {
          if ((err as Error).name !== "AbortError") setFailedFor(term);
        },
      );
    }, 250);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
  }, [term, selection.account]);

  // The Opportunity from a deep link is applied once, when its Account's Opportunities arrive.
  // Salesforce buttons merge 15-character IDs while the API returns 18, so match on the first 15
  // (the case-sensitive part both share) and select the listed ID.
  const [deepLink, setDeepLink] = useState(initialOpportunityId);
  const applyDeepLink = useEffectEvent((opps: OpportunityHit[]) => {
    const match = deepLink && opps.find((o) => o.id.slice(0, 15) === deepLink.slice(0, 15));
    if (match) {
      setDeepLink(undefined);
      onChange({ account: selection.account, opportunityId: match.id });
    }
  });
  useEffect(() => {
    if (!accountId) return;
    const ctl = new AbortController();
    fetchOpportunities(accountId, ctl.signal).then(
      (opps) => {
        setOppsFor({ accountId, opps });
        applyDeepLink(opps);
      },
      () => setOppsFor({ accountId, opps: [] }),
    );
    return () => ctl.abort();
  }, [accountId]);

  const showSearch = !selection.account && term.length >= 2;
  const hits = showSearch && found?.query === term ? found.hits : [];
  const searched = showSearch && found?.query === term;
  const failed = showSearch && failedFor === term;
  const opps = oppsFor && oppsFor.accountId === accountId ? oppsFor.opps : [];

  if (selection.account) {
    return (
      <div className={styles.picker}>
        <div className={styles.chosen}>
          <span className={styles.label}>Account</span>
          <strong>{selection.account.name}</strong>
          <button
            type="button"
            className={styles.link}
            onClick={() => {
              setQuery("");
              onChange(NO_SELECTION);
            }}
          >
            Change
          </button>
        </div>
        <label className={styles.label} htmlFor={oppId}>
          Opportunity (optional)
        </label>
        <select
          id={oppId}
          value={selection.opportunityId}
          onChange={(e) => onChange({ ...selection, opportunityId: e.target.value })}
        >
          <option value="">No Opportunity</option>
          {opps.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.stage ? ` (${o.stage})` : ""}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={styles.picker}>
      <label className={styles.label} htmlFor={searchId}>
        Account
      </label>
      <input
        id={searchId}
        type="search"
        autoComplete="off"
        placeholder="Search for the client Account by name"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {failed && (
        <p className={styles.note} role="alert">
          Accounts could not be searched. Please try again.
        </p>
      )}
      {hits.length > 0 && (
        <ul className={styles.hits} aria-label="Matching Accounts">
          {hits.map((a) => (
            <li key={a.id}>
              <button type="button" onClick={() => onChange({ account: a, opportunityId: "" })}>
                {a.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {searched && hits.length === 0 && !failed && (
        <p className={styles.note}>No Accounts match that name.</p>
      )}
    </div>
  );
}
